import { addItems } from '../cart/ajax_cart';

const boundForms = new WeakSet();
const boundVariantSelects = new WeakSet();

const getSelectedOptions = (volumeOptionPosition) => {
  const selected = {};
  const variantSelects = document.querySelector(
    'variant-selects[data-section], variant-radios[data-section]',
  );

  if (!variantSelects) return selected;

  variantSelects.querySelectorAll('select').forEach((select) => {
    const match = select.id.match(/Option-[^-]+-(\d+)$/);
    if (!match) return;
    const optionIndex = parseInt(match[1], 10) + 1;
    if (optionIndex === volumeOptionPosition) return;
    selected[`option${optionIndex}`] = select.value;
  });

  variantSelects
    .querySelectorAll('fieldset[data-option-position]')
    .forEach((fieldset) => {
      const optionIndex = parseInt(fieldset.dataset.optionPosition, 10);
      if (optionIndex === volumeOptionPosition) return;
      const checked = fieldset.querySelector('input:checked');
      if (checked) selected[`option${optionIndex}`] = checked.value;
    });

  return selected;
};

const variantMatchesSelection = (
  variant,
  volumeValue,
  volumeOptionPosition,
  selected,
) => {
  const volumeKey = `option${volumeOptionPosition}`;
  if (variant[volumeKey] !== volumeValue) return false;

  return Object.entries(selected).every(([key, value]) => variant[key] === value);
};

const updateVolumeRowVariantIds = () => {
  const volumeContainer = document.querySelector('.product__volume-variants');
  if (!volumeContainer) return;

  const variantsScript = volumeContainer.querySelector(
    '[data-volume-variants-json]',
  );
  if (!variantsScript) return;

  const variants = JSON.parse(variantsScript.textContent);
  const volumeOptionPosition = parseInt(
    volumeContainer.dataset.volumeOptionPosition,
    10,
  );
  const selected = getSelectedOptions(volumeOptionPosition);

  volumeContainer.querySelectorAll('.volume-variant-row').forEach((row) => {
    const volumeValue = row.dataset.volumeValue;
    const matched = variants.find((variant) =>
      variantMatchesSelection(
        variant,
        volumeValue,
        volumeOptionPosition,
        selected,
      ),
    );

    if (!matched) return;

    row.dataset.variantId = matched.id;
    row.querySelectorAll('[data-variant-id]').forEach((el) => {
      el.dataset.variantId = matched.id;
    });

    const qtyInput = row.querySelector('.quantity__input');
    if (!qtyInput) return;

    qtyInput.disabled = !matched.available;
    row.querySelectorAll('.quantity__button').forEach((btn) => {
      btn.disabled = !matched.available;
    });
  });
};

const buildSubmitHandler = (volumeContainer, form, submitButton) => {
  const cart =
    document.querySelector('cart-notification') ||
    document.querySelector('cart-drawer');
  const notification = document.querySelector('cart-notification-popup');

  return (event) => {
    if (!document.body.contains(volumeContainer)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const products = [];
    volumeContainer.querySelectorAll('.quantity__input').forEach((input) => {
      const quantity = parseInt(input.value, 10);
      const productId = input.dataset.variantId;
      if (quantity > 0 && productId) {
        products.push({ id: productId, quantity });
      }
    });

    if (!products.length || !submitButton) return;

    const sections = cart
      ?.getSectionsToRender()
      .map((section) => section.id)
      .toString();

    submitButton.setAttribute('aria-disabled', 'true');
    submitButton.classList.add('loading');
    submitButton
      .querySelector('.loading-overlay__spinner')
      ?.classList.remove('hidden');

    addItems(products, sections, (response) => {
      try {
        const parsed = JSON.parse(response);

        if (cart?.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }

        cart?.renderContents(parsed);

        // COB2B-811: the PDP confirms with the cart-notification-popup card, the same
        // as the product cards, and no longer opens the cart drawer. Only a real add
        // gets the card — a 422 ({status, description}) has no items.
        if (Array.isArray(parsed.items)) notification?.addMessage(parsed);

        volumeContainer
          .querySelectorAll('.quantity__input')
          .forEach((input) => {
            input.value = 0;
          });
      } catch (error) {
        console.error(error);
      } finally {
        submitButton.removeAttribute('aria-disabled');
        submitButton.classList.remove('loading');
        submitButton
          .querySelector('.loading-overlay__spinner')
          ?.classList.add('hidden');
      }
    });
  };
};

const bindVolumeBehaviour = () => {
  const volumeContainer = document.querySelector('.product__volume-variants');
  if (!volumeContainer) return false;

  const productForm = document.querySelector('product-form');
  const form = productForm?.querySelector('form');
  if (!form) return false;

  updateVolumeRowVariantIds();

  const variantSelects = document.querySelector(
    'variant-selects[data-section], variant-radios[data-section]',
  );
  if (variantSelects && !boundVariantSelects.has(variantSelects)) {
    variantSelects.addEventListener('change', updateVolumeRowVariantIds);
    boundVariantSelects.add(variantSelects);
  }

  if (!boundForms.has(form)) {
    const submitButton = form.querySelector('[type="submit"]');
    form.addEventListener(
      'submit',
      buildSubmitHandler(volumeContainer, form, submitButton),
      true,
    );
    boundForms.add(form);
  }

  return true;
};

const observeSectionSwaps = () => {
  // Shopify VariantSelects.renderProductInfo() replaces the entire
  // #GlobalProductSection-{id} innerHTML on non-Title option change.
  // All previously bound listeners are dropped with the old DOM.
  // Re-bind volume behaviour whenever children change.
  const sections = document.querySelectorAll(
    '[id^="GlobalProductSection-"]',
  );
  sections.forEach((section) => {
    const observer = new MutationObserver(() => {
      bindVolumeBehaviour();
    });
    observer.observe(section, { childList: true, subtree: true });
  });
};

const volumeVariants = () => {
  if (!document.querySelector('.product__volume-variants')) return;
  bindVolumeBehaviour();
  observeSectionSwaps();
};

export default volumeVariants;
